import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

function About() {
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="about">
      <section className="about__hero" aria-labelledby="about-title">
        <div className="about__hero-media">
          <img
            src="/main/woman-original.jpg"
            alt="Woman walking beside a bright blue wall"
            decoding="async"
          />
        </div>
        <div className="about__hero-copy">
          <p className="about__eyebrow">Our story</p>
          <h1 id="about-title">Denim as a daily practice.</h1>
          <p>
            {siteConfig.brandName} approaches denim through proportion, touch and
            the lives clothes enter after they leave the studio. Each collection
            begins with a simple question: can a familiar garment feel
            more useful, more expressive and more considered?
          </p>
          <Link to="/shop" onClick={toTop} className="about__text-link">
            Explore the collection
          </Link>
        </div>
      </section>

      <section className="about__manifesto" aria-labelledby="manifesto-title">
        <p className="about__eyebrow">Working principles</p>
        <h2 id="manifesto-title">
          Contemporary clothing, shaped by craft rather than noise.
        </h2>
        <p>
          We develop relaxed silhouettes that leave room for movement and
          character. Wash, weight and construction are treated as design tools:
          never decoration for its own sake, always part of how a piece wears,
          softens and becomes personal over time.
        </p>
      </section>

      <section className="about__editorial" aria-label="Our approach">
        <article className="about__story about__story--craft">
          <div className="about__story-media">
            <img
              src="/main/repair-original.jpg"
              alt="Hands repairing a garment on a worktable"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="about__story-copy">
            <p className="about__eyebrow">01 / Craft</p>
            <h2>Built from the cloth outward.</h2>
            <p>
              A garment is resolved through many small decisions: the density of
              a stitch, the fall of a leg, the placement of a pocket and the hand
              of a wash. We work iteratively with specialist makers, refining fit
              on the body before a style joins the collection.
            </p>
            <p>
              The result is clothing designed to be reached for often—not kept
              for an imagined occasion.
            </p>
          </div>
        </article>

        <article className="about__story about__story--material">
          <div className="about__story-copy">
            <p className="about__eyebrow">02 / Material</p>
            <h2>Fewer shortcuts. Better questions.</h2>
            <p>
              We favour fibres and processes selected for durability, comfort
              and a lower-impact direction. That work is ongoing. Rather than
              treating sustainability as a finished claim, we examine where a
              material comes from, how it is processed and what information can
              be carried forward with the garment.
            </p>
            <p>
              Longevity remains the first measure: thoughtful care, repair and
              repeated wear give good denim its richest life.
            </p>
          </div>
          <div className="about__story-media">
            <img
              src="/main/materials-original.jpg"
              alt="Colourful fabric rolls arranged on blue shelves"
              loading="lazy"
              decoding="async"
            />
          </div>
        </article>
      </section>

      <section className="about__values" aria-labelledby="values-title">
        <div className="about__values-heading">
          <p className="about__eyebrow">03 / Transparency</p>
          <h2 id="values-title">Progress should be visible.</h2>
        </div>
        <div className="about__values-grid">
          <article>
            <span>Trace</span>
            <h3>Know the chain</h3>
            <p>
              We ask for clearer information about fibres, mills, makers and
              finishing so choices can be assessed with greater context.
            </p>
          </article>
          <article>
            <span>Wear</span>
            <h3>Design for return</h3>
            <p>
              Versatile cuts, robust construction and honest materials are
              intended to earn a regular place in the wardrobe.
            </p>
          </article>
          <article>
            <span>Share</span>
            <h3>State what we know</h3>
            <p>
              Clear product information matters. Where knowledge is incomplete,
              we prefer specificity about the work still to do.
            </p>
          </article>
        </div>
      </section>

      <section className="about__contact" aria-label="Contact client care">
        <p className="about__eyebrow">Keep the conversation moving</p>
        <h2>Questions about a garment, its fit or its care?</h2>
        <Link to="/contact" onClick={toTop} className="about__button">
          Contact client care
        </Link>
      </section>
    </main>
  );
}

export default About;
